import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Paper,
  Rating,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { providersApi } from '../services/api';

/**
 * Admin review moderation (verified-provider-reviews design §7).
 *
 * Reads the ADMIN projection, which deliberately includes hidden reviews and hidden or withdrawn
 * replies. Every other review read in the product filters them; this one must not, or hiding a
 * review would be irreversible -- this is the only screen able to inspect the audit trail or
 * restore it.
 *
 * Review and reply visibility are INDEPENDENT, because an accurate review can draw an abusive or
 * customer-identifying reply, and hiding the review to suppress the reply punishes the wrong
 * party.
 */
export default function AdminReviewsTab({ providerId }) {
  const [rows, setRows] = useState(null);
  const [error, setError] = useState('');
  // { reviewId, target: 'review' | 'reply', hidden } while the reason dialog is open.
  const [pending, setPending] = useState(null);
  const [reason, setReason] = useState('');

  const load = useCallback(async () => {
    setError('');
    try {
      setRows(await providersApi.getProviderReviewsForAdmin(providerId));
    } catch (e) {
      setError('Could not load reviews.');
      setRows([]);
    }
  }, [providerId]);

  useEffect(() => { load(); }, [load]);

  const openDialog = (reviewId, target, hidden) => {
    setPending({ reviewId, target, hidden });
    setReason('');
  };

  const confirm = async () => {
    if (!reason.trim()) return;
    try {
      if (pending.target === 'reply') {
        await providersApi.setReviewReplyVisibility(pending.reviewId, pending.hidden, reason.trim());
      } else {
        await providersApi.setReviewVisibility(pending.reviewId, pending.hidden, reason.trim());
      }
      setPending(null);
      setReason('');
      await load();
    } catch (e) {
      setError('Could not update visibility.');
    }
  };

  if (rows === null) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>;
  }

  return (
    <Paper sx={{ p: 3, mb: 3 }}>
      <Typography variant="h5" component="h2" gutterBottom>Reviews</Typography>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {rows.length === 0 ? (
        <Typography variant="body1" color="text.secondary">No reviews yet.</Typography>
      ) : (
        <Stack divider={<Divider />} spacing={2}>
          {rows.map((row) => (
            <Box key={row.id} sx={{ display: 'flex', gap: 2, opacity: row.hidden ? 0.55 : 1 }}>
              <Avatar src={row.reviewerAvatarUrl || undefined}>
                {(row.reviewerName || '?').charAt(0)}
              </Avatar>
              <Box sx={{ flex: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                  <Typography variant="subtitle2">{row.reviewerName || 'Unnamed'}</Typography>
                  <Rating value={row.rating} readOnly size="small" />
                  <Typography variant="caption" color="text.secondary">
                    {row.createdAt ? new Date(row.createdAt).toLocaleDateString() : ''}
                  </Typography>
                  {/* Bound to a completed booking; legacy and seeded rows are not. */}
                  {row.verified && (
                    <Chip size="small" color="success" variant="outlined" label="Verified" />
                  )}
                  {row.hidden && <Chip size="small" color="warning" label="Hidden by admin" />}
                </Box>

                {row.comment && (
                  <Typography variant="body2" sx={{ mt: 0.5 }}>{row.comment}</Typography>
                )}

                {row.hidden && (
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                    {`Hidden by ${row.hiddenByName || 'an admin'}`}
                    {row.hiddenAt ? ` on ${new Date(row.hiddenAt).toLocaleDateString()}` : ''}
                    {row.hiddenReason ? ` — ${row.hiddenReason}` : ''}
                  </Typography>
                )}

                {row.reply && (
                  <Box
                    sx={{
                      mt: 1,
                      p: 1.5,
                      bgcolor: 'action.hover',
                      borderRadius: 1,
                      opacity: row.reply.hidden || row.reply.withdrawnAt ? 0.55 : 1,
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                      <Typography variant="caption" fontWeight={700}>Provider reply</Typography>
                      {row.reply.hidden && <Chip size="small" color="warning" label="Reply hidden" />}
                      {/* Withdrawn by the provider, not hidden by an admin -- a different fact. */}
                      {row.reply.withdrawnAt && <Chip size="small" label="Withdrawn" />}
                    </Box>
                    <Typography variant="body2">{row.reply.body}</Typography>
                    {row.reply.hidden && (
                      <Typography variant="caption" color="text.secondary" display="block">
                        {`Hidden by ${row.reply.hiddenByName || 'an admin'}`}
                        {row.reply.hiddenReason ? ` — ${row.reply.hiddenReason}` : ''}
                      </Typography>
                    )}
                    <Button
                      size="small"
                      onClick={() => openDialog(row.id, 'reply', !row.reply.hidden)}
                    >
                      {row.reply.hidden ? 'Show reply' : 'Hide reply'}
                    </Button>
                  </Box>
                )}

                <Button
                  size="small"
                  color={row.hidden ? 'primary' : 'error'}
                  sx={{ mt: 1 }}
                  onClick={() => openDialog(row.id, 'review', !row.hidden)}
                >
                  {row.hidden ? 'Show review' : 'Hide review'}
                </Button>
              </Box>
            </Box>
          ))}
        </Stack>
      )}

      <Dialog open={pending !== null} onClose={() => setPending(null)}>
        <DialogTitle>
          {`${pending?.hidden ? 'Hide' : 'Restore'} ${pending?.target === 'reply' ? 'reply' : 'review'}`}
        </DialogTitle>
        <DialogContent>
          {/* Required: the reason is written to the audit columns and survives a later restore. */}
          <TextField
            autoFocus
            fullWidth
            margin="dense"
            label="Reason"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPending(null)}>Cancel</Button>
          <Button onClick={confirm} disabled={!reason.trim()} variant="contained">Confirm</Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
}
